import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'stats.business.read',
  category: 'stats',
  title: 'Suivre son activité dans les statistiques',
  intent:
    'Lire, sur la période de votre choix, vos rendez-vous (honorés, non présentés, annulés, à venir), ce que valent vos réservations, vos clients (nouveaux, fidèles, à relancer), vos ventes et cartes cadeaux, vos avis Google et vos campagnes, avec des constats qui proposent une action.',
  context: {
    why: "Un indépendant pilote souvent son activité au ressenti : « ce mois-ci c'était calme », « j'ai eu beaucoup d'absents ». La page Statistiques met des chiffres sur ces impressions, à partir de ce qui est déjà dans Propelsia (agenda, encaissements, clients, avis, campagnes), sans rien saisir. Elle répond aux questions concrètes : combien de rendez-vous ont vraiment eu lieu, combien de clients ne sont pas revenus depuis trois mois, ce qu'il reste à encaisser sur les rendez-vous à venir, quelle part des réservations vient de Google ou du site. Chaque constat qui mérite une action (absences répétées, clients inactifs, avis sans réponse) propose le remède : un réglage déjà inclus dans votre formule, ou le bénéfice de la formule supérieure.",
    use_cases: [
      "Vérifier chaque lundi ce que la semaine a donné et ce qui est prévu : rendez-vous, chiffre d'affaires prévu, reste à encaisser.",
      'Repérer un taux de clients non présentés qui monte et activer un acompte ou une empreinte bancaire.',
      "Retrouver les clients qui ne sont pas revenus depuis 90 jours et lancer une relance ciblée d'un clic.",
      'Comparer un trimestre à celui d’avant (formules Pro et Max) pour voir si l’activité progresse.',
      'Savoir quelles prestations remplissent l’agenda et ce qu’elles valent (Pro), puis par membre et par lieu (Max).',
    ],
  },
  required_scopes: ['stats.read'],
  ui_entry_points: [
    {
      route: '/statistics',
      element:
        'Menu latéral « Statistiques » (propriétaire et gérant) : onglets Activité, Clients, Ventes, Avis Google, Campagnes',
    },
    { route: '/', element: 'Widgets « Aujourd’hui », « Cette semaine » et « À traiter » en haut de l’accueil' },
  ],
  api: {
    primary_operation_id: 'StatsBusinessAppController_getOverview',
    related_operation_ids: [
      'StatsBusinessAppController_getTimeseries',
      'StatsBusinessAppController_getAgenda',
      'StatsBusinessAppController_getCustomers',
      'StatsBusinessAppController_getSales',
      'StatsBusinessAppController_getReviews',
      'StatsBusinessAppController_getCampaigns',
      'StatsBusinessAppController_getBreakdown',
      'StatsBusinessAppController_getOccupancy',
      'StatsBusinessAppController_getHome',
    ],
  },
  steps: [
    '**Ouvrez « Statistiques »** dans le menu latéral. L’onglet Activité s’affiche sur les 30 derniers jours.',
    '**Choisissez la période** avec les pastilles : aujourd’hui, cette semaine, 7 ou 30 jours. Les formules Pro et Max ajoutent 3, 6 et 12 mois, une plage personnalisée et la comparaison avec la période précédente.',
    '**Lisez les indicateurs** : chaque carte porte un « ? » qui explique exactement ce qui est compté. « Reste à encaisser » et « CA prévu » sont deux chiffres différents : ne les additionnez pas.',
    '**Agissez sur les constats** : quand un bandeau signale des absents, des clients inactifs ou des avis sans réponse, son bouton vous emmène au réglage ou à l’action qui règle le problème.',
    '**Parcourez les onglets** : Clients (nouveaux, fidèles, inactifs à relancer), Ventes (encaissé, panier moyen, cartes cadeaux), Avis Google, Campagnes (envoyées, ouvertures, clics, et sur Pro et Max les réservations qu’elles ont apportées).',
    '**Exportez en CSV** (formules Pro et Max, depuis un ordinateur) pour votre comptable ou votre tableur.',
    '**Depuis l’accueil**, les trois widgets donnent le résumé du jour, de la semaine et de ce qui reste à traiter ; « Voir mes statistiques » ouvre la page sur la semaine en cours.',
  ],
  gotchas: [
    '**Un rendez-vous honoré est un rendez-vous passé, confirmé et non marqué « client non présenté ».** Marquez vos absents depuis la fiche du rendez-vous : c’est ce marquage qui alimente le taux de non présentés.',
    '**« Réservations prises » compte des réservations, « RDV honorés » compte des rendez-vous.** Une réservation peut couvrir plusieurs rendez-vous (séance de groupe, série récurrente).',
    '**Les montants sont TTC, frais de paiement non déduits, remboursements déduits.** Les règlements faits sur place sans être enregistrés dans Propelsia ne sont pas comptés.',
    '**« Nouveaux clients » suit la date « client depuis » de la fiche**, pas la date de création dans l’outil : un client importé garde son ancienneté réelle.',
    '**La provenance précise des réservations est suivie depuis le 2 octobre 2026.** Avant cette date, les réservations en ligne sont regroupées sous « Réservation en ligne ».',
    '**Les statistiques sont réservées au propriétaire et au gérant.** Les autres membres de l’équipe ne voient ni la page ni les widgets.',
    '**En période d’essai vous voyez tout** (historique 12 mois, comparaison, détail par prestation, membre et lieu, export). À la souscription Starter, ces éléments restent visibles mais verrouillés, avec le bénéfice de la formule qui les inclut.',
  ],
  related_features: ['agenda.appointment.no_show', 'customers.tag.manage', 'marketing.campaign.email.create'],
  demo_script: {
    duration_target_s: 40,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Mettons des chiffres sur votre activité : rendez-vous, clients, ventes, sans rien saisir.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/statistics'),
        caption: 'Menu latéral, Statistiques.',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.stats_kpi_card,
        caption: 'L’activité des 30 derniers jours s’affiche.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.stats_period_pill,
        caption: 'On change de période d’un clic : aujourd’hui, la semaine, 7 ou 30 jours.',
        capture: true,
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.stats_kpi_card,
        caption: 'Chaque carte explique ce qu’elle compte ; les constats proposent une action.',
        capture: true,
      },
    ],
  },
});
